// C:\Users\User\Desktop\Bresco\bresco-franquicies-frontend\src\pages\Siniestros\SiniestrosViewDetail.js
import { useUser } from 'contexts/UserContext'
import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import { LuMoveLeft } from 'react-icons/lu'
import { useLocation } from 'wouter'
import SiniestrosFormDetail from 'forms/Siniestros/SiniestrosFormDetail'

const emptyFields = {
  id: 0,
  IDInterno: '',
  fecha_dia: '',
  ref_fast: '',
  estado_siniestro: '',
  comentarios: '',
  ref_albaran: '',
  fecha_siniestro: '',
  causa: '',
  importe_reclamado: '',
  franquicia_aplicable: '',
  importe_neto_cobrar: '',
  tipo_pago: '',
  delegacion_propietaria: '',
  nombre_franquiciado: '',
  descripcion_mercancia: '',
  peso_mercancia_kg: '',
  cuenta: '',
  concepto_factura: '',
  reclamacion: '',
  parte_extravio: '',
  albaran: '',
  factura_compra: '',
  denuncia: '',
  factura_venta: '',
  expediente_completo: '',

  // Auditoría (solo mostrar)
  created_at: '',
  updated_at: '',
  updated_by_full_name: ''
}

const SI_NO_KEYS = [
  'concepto_factura',
  'reclamacion',
  'parte_extravio',
  'albaran',
  'factura_compra',
  'denuncia',
  'factura_venta',
  'expediente_completo'
]

const normalizeFetched = (row) => {
  const out = { ...(row || {}) }

  if (out.IDInterno === null || out.IDInterno === undefined) out.IDInterno = ''

  for (const k of SI_NO_KEYS) {
    const v = out[k]
    if (v === null || v === undefined || v === '') {
      out[k] = ''
      continue
    }
    const s = String(v).trim().toUpperCase()
    out[k] = s === 'SI' ? 'SI' : s === 'NO' ? 'NO' : ''
  }

  const nullToEmpty = [
    'fecha_dia',
    'ref_fast',
    'estado_siniestro',
    'comentarios',
    'ref_albaran',
    'fecha_siniestro',
    'causa',
    'importe_reclamado',
    'franquicia_aplicable',
    'importe_neto_cobrar',
    'tipo_pago',
    'delegacion_propietaria',
    'nombre_franquiciado',
    'descripcion_mercancia',
    'peso_mercancia_kg',
    'cuenta',
    'margen_porcentaje'
  ]
  for (const k of nullToEmpty) {
    if (out[k] === null || out[k] === undefined) out[k] = ''
  }

  // Auditoría: asegurar strings
  if (out.created_at === null || out.created_at === undefined) out.created_at = ''
  if (out.updated_at === null || out.updated_at === undefined) out.updated_at = ''
  if (out.updated_by_full_name === null || out.updated_by_full_name === undefined)
    out.updated_by_full_name = ''
  out.updated_by_full_name = String(out.updated_by_full_name || '').trim()

  return out
}

export default function SiniestrosViewDetail({ params }) {
  const { showToast } = useToast()
  const { user } = useUser()
  const [, setLocation] = useLocation()

  const [fetched, fetchOne] = useFetch()
  const [saved, saveOne] = useFetch()
  const [deleted, deleteOne] = useFetch()

  const isNew = params?.id === 'new'
  const [fields, setFields] = useState(emptyFields)
  const ref = useRef()
  const hasRedirectedAfterSave = useRef(false)

  // Cargar registro (o limpiar si es nuevo)
  useEffect(() => {
    if (!isNew) {
      fetchOne({
        url: `siniestros/${params.id}`,
        method: 'GET',
        messageKo: 'Error cargando siniestro'
      })
    } else {
      setFields(emptyFields)
    }
  }, [isNew, params?.id, fetchOne])

  // Aplicar datos o redirigir si error
  useEffect(() => {
    if (fetched.data) setFields({ ...emptyFields, ...normalizeFetched(fetched.data) })
    if (fetched.error) setLocation('/page-not-found')
  }, [fetched.data, fetched.error, setLocation])

  // POST / PUT
  const handleSubmit = async (values) => {
    let userId = Number(user?.id)

    if (!userId || Number.isNaN(userId)) {
      try {
        const raw = localStorage.getItem('user')
        const parsed = raw ? JSON.parse(raw) : null
        userId = Number(parsed?.id)
      } catch {
        // ignore
      }
    }

    if (!userId || Number.isNaN(userId)) {
      showToast('No se ha podido identificar el usuario de sesión.', 'error')
      return
    }

    let url = `siniestros/${userId}`
    let method = 'POST'

    const payload = { ...values }
    delete payload.id

    // no enviar auditoría
    delete payload.created_at
    delete payload.updated_at
    delete payload.updated_by_full_name

    if (!isNew) {
      method = 'PUT'
      url += `/${params.id}`
    } else {
      url += `/single`
    }

    await saveOne({ url, method, body: payload })
  }

// Resultado guardado
  useEffect(() => {
    if (!saved.isCalled) return
    if (saved.message) {
      if (hasRedirectedAfterSave.current) return
      hasRedirectedAfterSave.current = true

      showToast(saved.message, 'success')
      const newId = saved.data?.data?.id ?? saved.data?.id
      if (isNew && newId) {
        setLocation(`/siniestros/${newId}`)
      } else {
        setLocation('/siniestros/grid')
      }
    }
    if (saved.error) showToast(saved.error, 'error')
  }, [saved.isCalled, saved.message, saved.error, showToast, setLocation, isNew, saved.data])

  const handleDelete = async (id) => {
    const userId = Number(user?.id)
    if (!userId || Number.isNaN(userId)) {
      showToast('No se ha podido identificar el usuario de sesión.', 'error')
      return
    }

    await deleteOne({
      url: `siniestros/${userId}/${id}`,
      method: 'DELETE',
      messageKo: 'Error eliminando siniestro'
    })
  }

  // Resultado borrado
  useEffect(() => {
    if (!deleted.isCalled) return

    if (deleted.message) {
      showToast(deleted.message, 'success')
      setLocation('/siniestros/grid')
    }
    if (deleted.error) showToast(deleted.error, 'error')
  }, [deleted.isCalled, deleted.message, deleted.error, showToast, setLocation])

  return isNew || (!isNew && fields.id !== 0) ? (
    <>
      <div className="sm:flex sm:items-start sm:justify-between">
        <div className="flex items-start gap-x-3">
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">
            Siniestro ({fields?.IDInterno || (fields?.id !== 0 ? '#' + fields?.id : 'nuevo')})
          </h2>
        </div>

        <div className="flex items-center mt-4 sm:mt-0 gap-x-4">
          <button
            title="Volver"
            type="button"
            onClick={() => setLocation('/siniestros/grid')}
            className="flex items-center justify-center w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 gap-x-2 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-[#FCAE52] focus:border-[#FCAE52] dark:focus:border-[#FCAE52]"
          >
            <LuMoveLeft size={20} />
            <span>Volver</span>
          </button>
        </div>
      </div>

      <SiniestrosFormDetail
        isNew={isNew}
        user={user}
        fields={fields}
        siniestroRef={ref}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
      />
    </>
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p
        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
        title="Cargando..."
      >
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  )
}

SiniestrosViewDetail.propTypes = {
  params: PropTypes.any
}
